import React, { useCallback, useEffect, useState } from "react";
import { ActivityHistory, MasterDrawer, ViewOnlyNote } from "../Shared/master";
import { LOG_TYPES } from "../../helpers/activityLog";
import { showErrorToast } from "../../helpers/message";
import { isRequestCancelled } from "../../apis/useAxios";
import useAbortSignal from "../../hooks/useAbortSignal";
import useCrudActions from "../../hooks/useCrudActions";
import ConversationThread, { StatusChip } from "./ConversationThread";
import FeedbackPhotos from "./FeedbackPhotos";
import FeedbackReplyBox from "./FeedbackReplyBox";
import { FEEDBACK_TYPES, detailItems, formatDateTime, typeLabel } from "./feedbackRules";
import { getFeedbackDetail, replyToFeedback } from "./feedbackService";

const TABS = [
  { id: "details", label: "Feedback & replies", icon: "fas fa-comments" },
  { id: "history", label: "History", icon: "fas fa-history" },
];

/** Slider of one feedback: summary, details, photos, the conversation, the staff reply (Edit right) and its history. */
export default function FeedbackDrawer({ feedback: row, canEdit, onChanged, onClose }) {
  const getSignal = useAbortSignal();
  const [tab, setTab] = useState("details");
  const [opened, setOpened] = useState({ details: true });
  const [detail, setDetail] = useState(null);
  const [historyKey, setHistoryKey] = useState(0);
  const { busy, save } = useCrudActions();
  const feedback = detail ?? row;
  const type = FEEDBACK_TYPES[feedback.feedbackType];

  const load = useCallback(async () => {
    try {
      setDetail(await getFeedbackDetail(row.id, { signal: getSignal() }));
    } catch (err) {
      if (!isRequestCancelled(err)) showErrorToast(err?.message);
    }
  }, [row.id, getSignal]);

  useEffect(() => {
    load();
  }, [load]);

  const selectTab = (id) => {
    setTab(id);
    setOpened((prev) => ({ ...prev, [id]: true }));
  };

  const send = async ({ replyMsg, issueStatusId }) => {
    let sent = false;
    await save(() => replyToFeedback({ id: row.id, replyMsg, issueStatusId }), {
      onSuccess: async () => {
        sent = true;
        setHistoryKey((k) => k + 1);
        await Promise.all([load(), onChanged()]);
      },
    });
    return sent;
  };

  return (
    <MasterDrawer
      title={feedback.placeName || typeLabel(feedback)}
      subtitle={`${typeLabel(feedback)}${feedback.airportCode ? ` · ${feedback.airportCode}` : ""} · Feedback ${feedback.id}`}
      size="medium"
      showSave={false}
      onClose={onClose}
    >
      <div className="mst-range-tabs mst-range-tabs--sm mst-drawer-tabs" role="tablist" aria-label="Feedback">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={tab === t.id}
            className={`mst-range-tab${tab === t.id ? " is-selected" : ""}`}
            onClick={() => selectTab(t.id)}
          >
            <i className={t.icon} aria-hidden="true" />
            <span>{t.label}</span>
          </button>
        ))}
      </div>

      {tab === "details" && (
        <>
          <section className="fbk-summary" aria-label="Summary">
            <span className="fbk-summary__icon" aria-hidden="true">
              <i className={type?.icon ?? "fas fa-comment-dots"} />
            </span>
            <div className="fbk-summary__body">
              <div className="fbk-summary__chips">
                <StatusChip statusId={feedback.statusId} />
                {feedback.priority && <span className={`fbk-priority is-p${feedback.priorityId}`}>{feedback.priority} priority</span>}
                {feedback.issueType && <span className="fbk-tag">{feedback.issueType}</span>}
              </div>
              <span className="fbk-summary__meta">Sent {formatDateTime(feedback.createdAt) || "-"}</span>
            </div>
          </section>

          {!detail ? (
            <p className="fbk-loading">
              <i className="fas fa-spinner fa-spin" aria-hidden="true" /> Loading the details…
            </p>
          ) : (
            <>
              <dl className="fbk-details">
                {detailItems(detail).map((d) => (
                  <div key={d.label}>
                    <dt>{d.label}</dt>
                    <dd>{d.value}</dd>
                  </div>
                ))}
              </dl>
              {detail.supervisorNote && (
                <section className="fbk-section" aria-label="Supervisor note">
                  <h4>
                    <i className="fas fa-user-tie" aria-hidden="true" /> Supervisor note
                  </h4>
                  <p className="fbk-note">{detail.supervisorNote}</p>
                </section>
              )}
              <FeedbackPhotos photos={detail.photos ?? []} />
              <section className="fbk-section" aria-label="Conversation section">
                <h4>
                  <i className="fas fa-comments" aria-hidden="true" /> Conversation
                </h4>
                <ConversationThread feedback={detail} />
              </section>
              {canEdit ? <FeedbackReplyBox key={detail.statusId} feedback={detail} busy={busy} onSend={send} /> : <ViewOnlyNote />}
            </>
          )}
        </>
      )}
      {opened.history && (
        <div hidden={tab !== "history"}>
          <ActivityHistory logType={LOG_TYPES.feedback} columnValue1={String(row.id)} reloadKey={historyKey} />
        </div>
      )}
    </MasterDrawer>
  );
}
